/**
 * Base-path de la app — fuente única en el frontend.
 *
 * Todo se deriva de `import.meta.env.BASE_URL`, que Vite fija en build time a
 * partir de `--base` (o `base` en vite.config). Por defecto es "/tembiapo/";
 * al buildear para un dominio dedicado con `--base=/` pasa a ser "/".
 * `BASE_URL` SIEMPRE termina en "/".
 *
 * Con esto, un ÚNICO parámetro de build define basename del router, prefijo de
 * la API y resolución de assets/uploads, sin ningún "/tembiapo" hardcodeado.
 */

import { Capacitor } from '@capacitor/core';

/** Base con barra final: "/tembiapo/" o "/". */
export const BASE_URL = import.meta.env.BASE_URL;

/** basename del React Router (sin barra final): "/tembiapo/" → "/tembiapo"; "/" → "". */
export const ROUTER_BASENAME = BASE_URL.replace(/\/+$/, '');

/**
 * ¿Corremos dentro de la app nativa (Capacitor)? En web es `false`.
 *
 * Con el modelo `server.url` (ver capacitor.config), la app nativa carga la web
 * EN VIVO desde el mismo dominio de producción, así que corre en el MISMO ORIGEN
 * que la API y los uploads → se usan rutas RELATIVAS igual que en web. (El modelo
 * viejo empaquetaba los assets en https://localhost y llamaba a la API
 * cross-origin; eso rompía el login por CORS y quedó obsoleto.)
 */
export const IS_NATIVE = Capacitor.isNativePlatform();

/**
 * Prefijo de la API, relativo al base ("/tembiapo/api" o "/api"), tanto en web
 * como en la app nativa (misma-origen con `server.url`).
 */
export const API_BASE = `${BASE_URL}api`.replace(/\/{2,}/g, '/');

/**
 * Antepone el base a una ruta relativa de asset/upload. Deja intactas las URLs
 * absolutas (http/https, protocol-relative) y las que ya vienen bajo el base
 * (p.ej. lo que el backend arma con Config::basePath()).
 */
export function withBase(path: string): string {
  if (/^(https?:)?\/\//.test(path)) return path; // ya absoluta (http/https)
  if (path.startsWith(BASE_URL)) return path;
  return `${BASE_URL}${path.replace(/^\/+/, '')}`;
}
